/**
 * Deliverables Page Component
 * Static overview of the main HeatCraftHP project deliverables in chronological order
 * @module Deliverables
 */

import React from 'react';
import { Card, Collapse, Typography, Tag } from 'antd';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../PagesWrapper';
import styles from './Deliverables.module.css';
import LIFELogo from '../Shared/LIFE_logo.png';
import d17Pdf from './D1.7 Dedicated project page on the beneficiaries’ websites.pdf';
import d21Pdf from './D2.1 Certifications and Competencies Matrix.pdf';
import d21Excel from './T2.1 Certifications and Competencies.xlsx';
import d23Pdf from './D2.3 Skills gaps and training needs analysis.pdf';
import d31Pdf from './D3.1 Draft of Workplace Curriculum.pdf';
import d33Pdf from './D3.3 Game-based tool for learning.pdf';
import d37Pdf from './D3.7 Train the Trainer.pdf';
import d41Pdf from './D4.1 National implementation plans.pdf';

const { Title, Paragraph, Text } = Typography;

const deliverableOrder = [
  { key: 'd17', code: 'D1.7' },
  { key: 'd21', code: 'D2.1' },
  { key: 'd23', code: 'D2.3' },
  { key: 'd31', code: 'D3.1' },
  { key: 'd33', code: 'D3.3' },
  { key: 'd37', code: 'D3.7' },
  { key: 'd41', code: 'D4.1' }
];

const deliverablePdfLinks = {
  d17: d17Pdf,
  d21: d21Pdf,
  d23: d23Pdf,
  d31: d31Pdf,
  d33: d33Pdf,
  d37: d37Pdf,
  d41: d41Pdf
};

const deliverableExcelLinks = {
  d21: d21Excel
};

/**
 * Deliverables Page
 */
const Deliverables = () => {
  const { t } = useTranslation();

  const getSummaryParagraphs = (itemKey) => {
    const summary = t(`deliverables.items.${itemKey}.summary`, { returnObjects: true });
    return Array.isArray(summary) ? summary : [summary];
  };

  const collapseItems = deliverableOrder.map((item) => ({
    key: item.key,
    label: (
      <div className={styles.panelLabel}>
        <Tag color="blue" className={styles.codeTag}>{item.code}</Tag>
        <div className={styles.panelCopy}>
          <div className={styles.panelTitleRow}>
            <Title level={4} className={styles.panelTitle}>
              {t(`deliverables.items.${item.key}.title`)}
            </Title>
            {(deliverablePdfLinks[item.key] || deliverableExcelLinks[item.key]) && (
              <div className={styles.panelActions}>
                {deliverablePdfLinks[item.key] && (
                  <a
                    href={deliverablePdfLinks[item.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.pdfLink}
                    aria-label={`Open ${item.code} PDF in a new window`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <FilePdfOutlined className={styles.pdfIcon} />
                  </a>
                )}
                {deliverableExcelLinks[item.key] && (
                  <a
                    href={deliverableExcelLinks[item.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.pdfLink} ${styles.excelLink}`}
                    aria-label={`Open ${item.code} Excel file in a new window`}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <FileExcelOutlined className={styles.pdfIcon} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    children: (
      <div className={styles.panelBody}>
        {getSummaryParagraphs(item.key).map((paragraph, paragraphIndex) => (
          <Paragraph key={`${item.key}-${paragraphIndex}`} className={styles.bodyParagraph}>
            {paragraph}
          </Paragraph>
        ))}
      </div>
    )
  }));

  return (
    <PagesWrapper>
      <div className={styles.pageContainer}>
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <Title level={1} className={styles.heroTitle}>
              {t('deliverables.heroTitle')}
            </Title>
            <Paragraph className={styles.heroSubtitle}>
              {t('deliverables.heroSubtitle')}
            </Paragraph>
          </div>
        </section>

        <section className={styles.introSection}>
          <div className={styles.contentShell}>
            <Card className={styles.introCard}>
              <Title level={2} className={styles.introTitle}>
                {t('deliverables.introTitle')}
              </Title>
              <Paragraph className={styles.introParagraph}>
                {t('deliverables.introParagraph1')}
              </Paragraph>
              <Paragraph className={styles.introParagraph}>
                {t('deliverables.introParagraph2')}
              </Paragraph>
            </Card>
          </div>
        </section>

        <section className={styles.timelineSection}>
          <div className={styles.contentShell}>
            <div className={styles.sectionHeader}>
              <Title level={2} className={styles.sectionTitle}>
                {t('deliverables.timelineTitle')}
              </Title>
              <Paragraph className={styles.sectionDescription}>
                {t('deliverables.timelineSubtitle')}
              </Paragraph>
            </div>

            <Collapse
              defaultActiveKey={['d17']}
              expandIconPosition="end"
              className={styles.deliverablesAccordion}
              items={collapseItems}
            />

            <div className={styles.disclaimerBlock}>
              <div className={styles.footerDividerLine} />
              <div className={styles.footerDisclaimer}>
                <img
                  src={LIFELogo}
                  alt="Co-funded by the European Union"
                  className={styles.footerDisclaimerImage}
                />
                <Text className={styles.footerDisclaimerText}>
                  Co-funded by the European Union. Views and opinions expressed are however those of the
                  author(s) only and do not necessarily reflect those of the European Union or CINEA.
                  Neither the European Union nor the granting authority can be held responsible for them.
                </Text>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PagesWrapper>
  );
};

export default Deliverables;